import React, { useCallback, forwardRef, useMemo, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { Override } from 'types';
import { dateEquals, ParsableDate, dateParse } from 'components/utils/DateFormat';
import useFormInput, { FormInputProps } from './useFormInput';
import './FormInput.scss';

const TYPES = {
  date: {
    format: 'yyyy-MM-dd',
    units: function dateToUnits(date: DateTime) {
      return {
        year: date.get('year'),
        month: date.get('month'),
        day: date.get('day'),
      };
    },
    startOf: 'day',
  },
  time: {
    format: 'HH:mm',
    units: function dateToUnits(date: DateTime) {
      return {
        hour: date.get('hour'),
        minute: date.get('minute'),
      };
    },
    startOf: 'minute',
  },
} as const;

type BaseProps = FormInputProps<ParsableDate | null, never> & {
  type: keyof typeof TYPES;
  min?: ParsableDate;
  max?: ParsableDate;
};

export type DateTimeInputProps = Override<React.ComponentPropsWithoutRef<'input'>, BaseProps>;

function DateTimeInput(
  { type, min: tmpMin, max: tmpMax, ...props }: DateTimeInputProps,
  ref: React.Ref<HTMLInputElement>,
): JSX.Element {
  const { id, value, name, touched, error, className, onValueChange, onChange, ...otherProps } =
    useFormInput(props);
  const [textValue, setTextValue] = useState('');
  const TYPE = TYPES[type];

  // Be sure min and max are set
  const { placeholder } = useMemo(() => {
    const min = tmpMin
      ? dateParse(tmpMin, { setZone: true }).startOf(TYPE.startOf)
      : DateTime.local().startOf(TYPE.startOf).minus({ year: 50 });
    const max = tmpMax
      ? dateParse(tmpMax, { setZone: true }).startOf(TYPE.startOf)
      : DateTime.local().endOf(TYPE.startOf).plus({ year: 50 });

    const placeholder = TYPE.format;

    return {
      min: min.toFormat(TYPE.format),
      max: max.toFormat(TYPE.format),
      placeholder,
    };
  }, [TYPE, tmpMax, tmpMin]);

  const normalizedValue = useMemo(() => {
    const normalizedValue = dateParse(value, { setZone: true }).startOf('minute');
    if (normalizedValue.isValid) {
      return normalizedValue;
    }

    return null;
  }, [value]);

  const innerValueRef = useRef<DateTime | null>(null);
  const valueRef = useRef<DateTime | null>(normalizedValue);

  // This is used to store the last valid value and reuse its parts for the new value
  const lastValidValueRef = useRef(normalizedValue);

  // Update text value when value is updated
  useEffect(() => {
    valueRef.current = normalizedValue;
    if (!dateEquals(valueRef.current, innerValueRef.current)) {
      const textValue = valueRef.current ? valueRef.current.toFormat(TYPE.format) : '';
      setTextValue(textValue);
    }
  }, [TYPE.format, normalizedValue]);

  // Update value when text value is updated
  useEffect(() => {
    let resolvedValue = textValue
      ? DateTime.fromFormat(textValue, TYPE.format).startOf(TYPE.startOf)
      : null;

    // First check the text value was properly formatted
    resolvedValue = resolvedValue && resolvedValue.isValid ? resolvedValue : null;

    // Reuse previous valid value to keep other parts of the date
    const baseValue = valueRef.current ? valueRef.current : lastValidValueRef.current;
    if (resolvedValue && baseValue) {
      resolvedValue = baseValue.set(TYPE.units(resolvedValue));
    }

    // Store the definitive value to compare later with value received as prop
    innerValueRef.current = resolvedValue;

    // And keep valid value for parts
    if (innerValueRef.current && innerValueRef.current.isValid) {
      lastValidValueRef.current = innerValueRef.current;
    }

    // Only propagate value if the new value is different than the previous
    if (!dateEquals(innerValueRef.current, valueRef.current)) {
      const newValue = innerValueRef.current ? innerValueRef.current.toISO() : null;
      if (onValueChange) {
        onValueChange(newValue, name);
      }

      if (onChange) {
        const fakeEvent = {
          target: { name, id, value: newValue },
        };

        onChange(fakeEvent as unknown as React.FormEvent<HTMLInputElement>);
      }
    }
  }, [TYPE, id, name, onChange, onValueChange, textValue]);

  // Update the text value when input changes
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTextValue(value);
  }, []);

  return (
    <input
      ref={ref}
      id={id}
      type={type}
      className={classNames('FormInput', className)}
      placeholder={placeholder}
      name={name}
      {...otherProps}
      value={textValue}
      onChange={handleChange}
      // min={min}
      // max={max}
    />
  );
}

export default forwardRef(DateTimeInput);
